import React, { useState, useRef, useEffect, useCallback } from "react";
import heartLoading from "../../../assests/heartLoading.gif";
import BusinessTableHeader from "./BusinessTableHeader";
import Popup from "./Popup";

const parsePeriodWeekKey = (key) => {
  const match = key?.match(/^(\d{4})P(\d+)W(\d+)$/);
  if (!match) return null;
  return {
    year: match[1],
    period: `P${match[2].padStart(2, "0")}`,
    week: `W${match[3].padStart(2, "0")}`,
  };
};

const getInitials = (name = "") => {
  const clean = name.split("@")[0].replace(/[._-]/g, " ").trim();
  const parts = clean.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
};

const getAvatarColor = (name = "") => {
  const colors = [
    "bg-blue-500", "bg-emerald-500", "bg-violet-500",
    "bg-orange-500", "bg-pink-500", "bg-teal-500",
    "bg-rose-500", "bg-indigo-500",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const ProductsCell = ({ names = [] }) => {
  const [open, setOpen] = useState(false);
  const visible = names.slice(0, 2);
  const extra = names.length - visible.length;

  return (
    <>
      <div onClick={() => setOpen(true)} className="flex flex-wrap gap-1.5 items-center">
        {visible.map((name, i) => (
          <span
            key={i}
            title={name}
            className="inline-block max-w-[160px] truncate px-2.5 py-1 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-100"
          >
            {name}
          </span>
        ))}
        {extra > 0 && (
          <button
            onClick={() => setOpen(true)}
            className="px-2.5 py-1 text-xs font-semibold rounded-full bg-gray-100 text-[#bb1ccc] hover:bg-blue-50 border border-gray-200 transition-colors"
          >
            +{extra} more
          </button>
        )}
      </div>

      {open && (
        <Popup
          title="Downstream Products"
          items={names}
          type="text"
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const UsersCell = ({ names = [] }) => {
  const [open, setOpen] = useState(false);
  const visible = names.slice(0, 3);
  const extra = names.length - visible.length;

  return (
    <>
      <div className="flex items-center gap-1">
        <div onClick={() => setOpen(true)} className="flex -space-x-2">
          {visible.map((name, i) => (
            <span
              key={i}
              title={name}
              className={`w-[32px] h-[32px] rounded-full flex items-center justify-center text-white text-xs font-bold ring-2 ring-white ${getAvatarColor(name)}`}
            >
              {getInitials(name)}
            </span>
          ))}
        </div>
        {extra > 0 && (
          <button
            onClick={() => setOpen(true)}
            className="ml-1 w-[32px] h-[32px] rounded-full bg-gray-100 ring-2 ring-white text-xs font-semibold text-[#bb1ccc] hover:bg-blue-50 transition-colors flex items-center justify-center"
          >
            +{extra}
          </button>
        )}
      </div>

      {open && (
        <Popup
          title="Users"
          items={names}
          type="avatar"
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

export const BusinessUserTable = ({
  businessUser,
  sortBy = "",
  sortOrder = "asc",
  columnSearch = {},
  onSortChange,
  onSearchChange,
  totalRecords,
  loadMore,
  hasMore,
  isFetchingMore,
}) => {
  const sentinelRef = useRef(null);

  const handleIntersect = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
        loadMore();
      }
    },
    [hasMore, isFetchingMore, loadMore]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: "150px",
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  if (!businessUser || businessUser.length === 0) {
    return (
      <div className="flex items-center justify-center h-24 rounded-xl border border-gray-200 bg-gray-50 my-6">
        <p className="text-sm text-gray-400 italic">No Description Available</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 my-6">
      <div className="overflow-x-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">
            Business Users ({totalRecords ?? "—"})
          </h2>
          <span className="text-[13px] text-gray-500">
            {businessUser.length} of {totalRecords ?? "—"} loaded
          </span>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <BusinessTableHeader
              sortBy={sortBy}
              sortOrder={sortOrder}
              columnSearch={columnSearch}
              onSortChange={onSortChange}
              onSearchChange={onSearchChange}
            />
            <tbody>
              {businessUser.length > 0 ? (
                businessUser.map((user, index) => {
                  const parsed = parsePeriodWeekKey(user.week);
                  const productNames = user.product_names?.length
                    ? user.product_names
                    : user.datapulse_product_name
                      ? [user.datapulse_product_name]
                      : [];

                  return (
                    <tr
                      key={index}
                      className={`border-b border-gray-100 hover:bg-[#CCFBF1] transition-colors ${index === businessUser.length - 1 ? "border-b-0" : ""
                        }`}
                    >
                      <td className="px-5 py-3 text-gray-600">
                        {parsed?.year ?? "—"}
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {parsed?.period ?? "—"}
                      </td>
                      <td className="px-5 py-3 text-gray-600">
                        {parsed?.week ?? "—"}
                      </td>
                      <td className="px-5 py-3">
                        {productNames.length > 0 ? (
                          <ProductsCell names={productNames} />
                        ) : (
                          <span className="text-gray-400 italic text-xs">No Product Available</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {user.name?.length > 0 ? (
                          <UsersCell names={user.name} />
                        ) : (
                          <span className="text-gray-400 italic text-xs">A query was triggered, but the email ID could not be retrieved</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-gray-800 font-medium">
                        {user.count.toLocaleString()}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-6 text-gray-500 font-medium"
                  >
                    No Data found
                  </td>
                </tr>
              )}
              {/* ── Scroll sentinel ── */}
              <tr>
                <td colSpan={6} className="p-0">
                  <div ref={sentinelRef} style={{ height: 1 }} />
                  {isFetchingMore && (
                    <div className="flex items-center justify-center py-6 gap-2">
                      <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
                      <span className="text-gray-500 text-2xl">Loading more...</span>
                    </div>
                  )}
                  {!hasMore && businessUser.length > 0 && (
                    <div className="text-center py-3 text-gray-400 text-xs">
                      All {totalRecords} records loaded
                    </div>
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};